/// <amd-module name="Tfs/ProjectsDropdown/Components/ProjectsDropdown" />
import * as React from "react";

import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import * as Resources from "Tfs/ProjectsDropdown/Resources";
import { IReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { Dropdown, DropdownCallout, DropdownExpandableTextField, IDropdownCalloutProps } from "VSSUI/Dropdown";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IProjectsService } from "../ProjectsService";
import { IProjectDropdownProps } from "./ProjectsDropdown.Props";

export class ProjectsDropdown extends VssComponent<IProjectDropdownProps> {
    private selection: IListSelection = new DropdownSelection();
    private dropdown = React.createRef<Dropdown>();
    private items = new ObservableArray<IListBoxItem<TeamProjectReference>>();
    private allProjects: IReadonlyObservableArray<TeamProjectReference>;
    private initialProject?: IListBoxItem<TeamProjectReference>;
    private loading = new ObservableValue(true);
    private selectedProject?: TeamProjectReference;

    constructor(props: IProjectDropdownProps) {
        super(props);

        if (props.initialSelection) {
            this.selectedProject = props.initialSelection;
            this.initialProject = this.projectToListItem(props.initialSelection);
            this.items.push(this.initialProject);
            this.selection.select(0, 1);
        }

        const loadingItem = {
            id: "loading",
            text: Resources.Loading,
            type: ListBoxItemType.Loading
        };

        this.items.push(loadingItem);
    }

    public render(): JSX.Element | null {
        return (
            <Dropdown<TeamProjectReference>
                className="projects-dropdown"
                loading={this.loading}
                items={this.items}
                selection={this.selection}
                onSelect={this.onSelect}
                renderCallout={this.renderCallout}
            />
        );
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        if (this.allProjects) {
            this.allProjects.unsubscribe(this.onProjectsLoaded);
        }
    }

    public focus(): void {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    private renderCallout = (props: IDropdownCalloutProps<TeamProjectReference>) => {
        if (!this.allProjects) {
            const projectsService = this.context.pageContext.getService<IProjectsService>("IProjectsService");
            this.allProjects = projectsService.getAllProjects();
            if (this.allProjects.length) {
                // We already have items. Populate them and clear out the loading row
                this.onProjectsLoaded();

                if (props.filteredItems) {
                    // We need to set filtered items as well since we changed out the items during initial render.
                    props.filteredItems.value = this.items.value;
                }
            }
            this.allProjects.subscribe(this.onProjectsLoaded);
        }
        return DropdownCallout(props);
    };

    private onProjectsLoaded = () => {
        this.loading.value = false;

        const items: IListBoxItem<TeamProjectReference>[] = [];

        let hasInitialProject = false;

        if (this.allProjects && this.allProjects.length) {
            for (const project of this.allProjects.value) {
                items.push(this.projectToListItem(project));
                if (this.initialProject && this.initialProject.id === project.id) {
                    hasInitialProject = true;
                }
            }
        }

        if (!hasInitialProject && this.initialProject) {
            items.unshift(this.initialProject);
        }

        this.items.value = items;

        if (this.selectedProject) {
            this.selectProject(this.selectedProject);
        }
    };

    private selectProject(project: TeamProjectReference) {
        for (let i = 0, l = this.items.length; i < l; i++) {
            if (this.items.value[i].id === project.id) {
                this.selection.select(i, 1);
                return;
            }
        }

        // Project not found -- clear the selection
        this.selection.clear();
    }

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<TeamProjectReference>): void => {
        this.selectedProject = item.data;
        this.props.onSelectedProjectChanged(event, item.data!);
    };

    private projectToListItem(project: TeamProjectReference): IListBoxItem<TeamProjectReference> {
        return {
            id: project.id,
            iconProps: { iconName: "Work" },
            text: project.name,
            data: project
        };
    }
}
